import {
  createNewRepairService,
  getAllRepairServices,
  getRepairServiceById,
  listAllRepairServices,
  updateRepairService,
  updateManyRepairServices,
  getMatchingRepairServiceError,
  getRepairServiceByName,
} from '@/api/repair-service/repair-service';
import { TRepairServiceSchema } from '@/schemas/repair-service/repair-service.schema';
import { TCustomQueryProps } from '@/type/hooks.types';
import { ErrorResponse } from '@/utils/axios.http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EquipmentDmgFilterType } from '@/entities/equipment';
import { notify } from '@/utils/toast.utils';
import { NOTIFY } from '@/utils/constant.utils';

export const useCreateRepairService = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TRepairServiceSchema) => createNewRepairService(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['getAllRepairServices'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingRepairServiceError'],
      });
      queryClient.invalidateQueries({
        queryKey: ['listAllRepairServices'],
      });

      notify(t('repairServiceCreatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useGetMatchingRepairServiceError = (
  query?: TRepairServiceSchema,
) => {
  return useQuery({
    queryKey: ['getMatchingRepairServiceError', query],
    queryFn: () => getMatchingRepairServiceError(query),
  });
};

export const useListAllRepairServices = () => {
  return useQuery({
    queryKey: ['listAllRepairServices'],
    queryFn: () => listAllRepairServices(),
  });
};

export const useGetAllRepairServices = (
  filter: EquipmentDmgFilterType,
  page: number,
  limit: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc',
) => {
  return useQuery({
    queryKey: [
      'getAllRepairServices',
      filter,
      page,
      limit,
      sortBy,
      sortDirection,
    ],
    queryFn: () =>
      getAllRepairServices(filter, page, limit, sortBy, sortDirection),
  });
};

export const useGetRepairServiceById = (id: string) => {
  return useQuery({
    queryKey: ['getRepairServiceById', id],
    queryFn: () => getRepairServiceById(id),
    enabled: !!id,
  });
};

export const useGetRepairServiceByName = (name: string) => {
  return useQuery({
    queryKey: ['getRepairServiceByName', name],
    queryFn: () => getRepairServiceByName(name),
    enabled: !!name,
  });
};

export const useGetRepairServicesSelectOptions = () => {
  const { data: allRepairServices, isLoading: isLoadingAllRepairServices } =
    useListAllRepairServices();

  const getAllRepairServicesSelectOptions = () => {
    if (
      !isLoadingAllRepairServices &&
      allRepairServices &&
      allRepairServices?.data?.length > 0
    ) {
      return allRepairServices?.data?.map(
        (repairService: TRepairServiceSchema) => {
          return {
            label: repairService?.name,
            value: repairService?.id,
          };
        },
      );
    }

    return undefined;
  };

  return {
    getAllRepairServicesSelectOptions,
    isLoadingAllRepairServices,
  };
};

export const useUpdateRepairService = ({
  t,
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (repairService: TRepairServiceSchema) =>
      updateRepairService(repairService),
    onSuccess: (_, variables) => {
      const { id } = variables;

      queryClient.invalidateQueries({
        queryKey: ['getAllRepairServices'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingRepairServiceError'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getRepairServiceById', id],
      });

      notify(t('repairServiceUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (err: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      handleError?.(err);
      console.error('Error when updating repair service:', err.error);
    },
  });
};

export const useUpdateManyRepairServices = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (repairServices: TRepairServiceSchema[]) =>
      updateManyRepairServices(repairServices),
    onSuccess: (_, variables) => {
      for (const item of variables) {
        queryClient.invalidateQueries({
          queryKey: ['getRepairServiceById', item?.id],
        });
      }

      queryClient.invalidateQueries({
        queryKey: ['getAllRepairServices'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingRepairServiceError'],
      });

      notify(
        variables.length === 0
          ? t('common:theseElementsLinkedToEntitiesNotDeactivable')
          : variables.length > 1
            ? t('repairServicesUpdatedSuccessfully')
            : t('repairServiceUpdatedSuccessfully'),
        NOTIFY.SUCCESS,
      );

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating repair services:', error);
    },
  });
};
